import { createBrowserRouter, Navigate } from 'react-router';
import { ProtectedRoute } from './ProtectedRoute';
import { Shell } from '@/layout/Shell';
import { DashboardPage } from '@/pages/DashboardPage';
import { LoginPage } from '@/pages/LoginPage';
import { PasswordChangePage } from '@/pages/PasswordChangePage';
import { MfaEnrollPage } from '@/pages/MfaEnrollPage';
import { MfaVerifyPage } from '@/pages/MfaVerifyPage';
import { MfaRecoveryPage } from '@/pages/MfaRecoveryPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { TenantsListPage } from '@/pages/TenantsListPage';
import { TenantDetailPage } from '@/pages/TenantDetailPage';
import { OrderSearchPage } from '@/pages/OrderSearchPage';
import { OrderDetailPage } from '@/pages/OrderDetailPage';
import { TrialExtendPage } from '@/pages/TrialExtendPage';
import { ConcessionsPage } from '@/pages/ConcessionsPage';
import { TenantLifecyclePage } from '@/pages/TenantLifecyclePage';
import { AdjustmentsPage } from '@/pages/AdjustmentsPage';
import { DiscountsPage } from '@/pages/DiscountsPage';
import { OperatorsListPage } from '@/pages/OperatorsListPage';
import { OperatorDetailPage } from '@/pages/OperatorDetailPage';
import { ActivationPage } from '@/pages/ActivationPage';
import { FeatureFlagsListPage } from '@/pages/FeatureFlagsListPage';
import { FeatureFlagDetailPage } from '@/pages/FeatureFlagDetailPage';
import { PricingPage } from '@/pages/PricingPage';
import { VaptAiUsagePage } from '@/pages/VaptAiUsagePage';
import { CustomerAgentCostsPage } from '@/pages/CustomerAgentCostsPage';
import { PrintingFleetPage } from '@/pages/PrintingFleetPage';
import { AgentsPage } from '@/pages/AgentsPage';
import { AgentProposalsPage } from '@/pages/AgentProposalsPage';
import { TenantAgentWorkOrderPage } from '@/pages/TenantAgentWorkOrderPage';
import { AgentWorkOrdersBatchPage } from '@/pages/AgentWorkOrdersBatchPage';
import { McpAuthorizePage } from '@/pages/McpAuthorizePage';
import { MCP_AUTHORIZE_PATH } from '@/lib/mcp-consent';
import { AdminOnlyRoute } from './AdminOnlyRoute';
import { McpAuthorizeRoute } from './McpAuthorizeRoute';

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/login/password-change', element: <PasswordChangePage /> },
  { path: '/login/mfa-enroll', element: <MfaEnrollPage /> },
  { path: '/login/mfa-verify', element: <MfaVerifyPage /> },
  { path: '/login/mfa-recovery', element: <MfaRecoveryPage /> },

  // Anonymous activation flow — operator clicks the magic-link in their email
  // (design D2 / §4.8). No auth, no shell.
  { path: '/platform/activate', element: <ActivationPage /> },

  // Consentimento de concessão do MCP (change platform-mcp-server, S-2.16a): fora do Shell, com
  // a guarda que guarda a query do pedido antes do login; a página recusa quem não é Admin. Import
  // direto, como as demais: este router não usa lazy nem lazyWithReload.
  {
    path: MCP_AUTHORIZE_PATH,
    element: (
      <McpAuthorizeRoute>
        <McpAuthorizePage />
      </McpAuthorizeRoute>
    ),
  },

  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Shell />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'tenants', element: <TenantsListPage /> },
      { path: 'tenants/:tenantId', element: <TenantDetailPage /> },
      { path: 'tenants/:tenantId/trial', element: <TrialExtendPage /> },
      { path: 'tenants/:tenantId/adjustments', element: <AdjustmentsPage /> },
      { path: 'tenants/:tenantId/discounts', element: <DiscountsPage /> },
      { path: 'tenants/:tenantId/concessions', element: <ConcessionsPage /> },
      { path: 'tenants/:tenantId/lifecycle', element: <AdminOnlyRoute><TenantLifecyclePage /></AdminOnlyRoute> },
      { path: 'tenants/:tenantId/orders/:orderId', element: <OrderDetailPage /> },
      // Ordens de serviço do agente de escrita (change platform-mcp-writes, 1.17): a da loja e o
      // lote. A API confere a permissão da ação equivalente; a tela só esconde o que o papel não faz.
      { path: 'tenants/:tenantId/agente', element: <TenantAgentWorkOrderPage /> },
      { path: 'lojas-para-agir', element: <AgentWorkOrdersBatchPage /> },
      { path: 'orders/search', element: <OrderSearchPage /> },
      { path: 'precos', element: <PricingPage /> },
      { path: 'vapt-ia', element: <VaptAiUsagePage /> },
      {
        // Custos do Atendente IA (change billing-ai-units, decisão 27): tabela da Meta e tetos em
        // dólar, só Admin. Import direto, como as demais.
        path: 'atendente-ia',
        element: (
          <AdminOnlyRoute>
            <CustomerAgentCostsPage />
          </AdminOnlyRoute>
        ),
      },
      { path: 'impressao', element: <PrintingFleetPage /> },
      // Propostas de agentes (change platform-mcp-writes, 1.19): todo papel abre; a permissão de
      // cada tipo é conferida pela API na aprovação. Import direto, como as demais.
      { path: 'propostas-agentes', element: <AgentProposalsPage /> },
      { path: 'profile', element: <ProfilePage /> },
      {
        path: 'operators',
        element: (
          <AdminOnlyRoute>
            <OperatorsListPage />
          </AdminOnlyRoute>
        ),
      },
      {
        path: 'operators/:id',
        element: (
          <AdminOnlyRoute>
            <OperatorDetailPage />
          </AdminOnlyRoute>
        ),
      },
      {
        // Agentes do MCP (change platform-mcp-server, S-2.16). Import direto, como as demais:
        // este router não usa lazy nem lazyWithReload.
        path: 'agentes',
        element: (
          <AdminOnlyRoute>
            <AgentsPage />
          </AdminOnlyRoute>
        ),
      },
      {
        path: 'feature-flags',
        element: (
          <AdminOnlyRoute>
            <FeatureFlagsListPage />
          </AdminOnlyRoute>
        ),
      },
      {
        path: 'feature-flags/:key',
        element: (
          <AdminOnlyRoute>
            <FeatureFlagDetailPage />
          </AdminOnlyRoute>
        ),
      },
    ],
  },

  { path: '*', element: <Navigate to="/" replace /> },
]);
